// Lib
import React, { useCallback, useMemo } from 'react';
import { useDispatch } from 'react-redux';

// Components
import Details from '../../../Products/Details';

// Actions
import { OpenModal } from '../../../../actions/modal';

// Hooks
import useCart from '../../../../pages/Cart/hooks/useCart';
import useUI from '../../../../hooks/useUI';
import useUser from '../../../../hooks/useUser';
import useOverLimitProduct from '../../../../pages/Cart/hooks/useOverLimitProduct';
import useKitsProducts from '../../../../pages/Kits/hooks/useKitsProducts';
import useKitActions from '../../../../pages/Kits/hooks/useKitActions';
import useSaveForLater from '../../../../pages/Cart/hooks/useSaveForLater';
import useUnavailableForOrderProduct from '../../../../hooks/useUnavailableForOrderProduct';

// Styles
import { useStyles } from './CartCardStyles';

// Constants
import { ORDER_APPROVAL_STATUSES } from '../../../../constants/approvals';
import { isOverLimit } from '../../../../utils/overLimit';

const getPendingApproval = (isRecurring, placedOrder, stockNumber) => {
  if (isRecurring) {
    return false;
  }
  const item = placedOrder?.order_items?.find(
    (item) => item.product.product_id === stockNumber,
  );
  return item?.status === ORDER_APPROVAL_STATUSES.WAITING_FOR_APPROVAL;
};

const getOrderedProduct = (isRecurring, orderItems, stockNumber) =>
  orderItems?.find((item) =>
    isRecurring
      ? item.product === stockNumber
      : item.product.product_id === stockNumber,
  );

const getTotalItemsHeader = (reviewOrderPage, isDesktopMode) => {
  if (reviewOrderPage && isDesktopMode) {
    return 'Total Items Per Order';
  }
  if (reviewOrderPage) {
    return 'Items Per Order';
  }
  return 'Total Items';
};

const getUnavailability = (kitItemOrderable, computedAvailability) =>
  kitItemOrderable
    ? {
        isUnavailableForOrder: false,
        unavailableLabel: '',
        unavailableWatermark: <></>,
      }
    : computedAvailability;

const useCartCard = ({
  product,
  reviewOrderPage,
  placedOrder,
  addressesCount = 1,
  kit,
  kitItemOrderable,
  fromSharedKit,
  onClearOverLimitInDB,
  onSaveOverLimitInDB,
  readOnly,
}) => {
  const { isKiteUser } = useUser();
  const dispatch = useDispatch();
  const { isDesktopMode, isTabletMode, isMobileMode } = useUI();
  const {
    count,
    isProductMoreThanInStock,
    addOne,
    removeOne,
    setCount,
    onDeleteProduct,
  } = useCart(product?.product_id);

  const removeOneProduct = useCallback(
    (product) => () => {
      removeOne(product)();
      if (!isOverLimit(product, count - 1, 1)) {
        onClearOverLimitInDB(product);
      }
    },
    [removeOne, count, onClearOverLimitInDB],
  );

  const addOneProduct = useCallback(
    (product) => () => {
      if (isOverLimit(product, count + 1, 1)) {
        onSaveOverLimitInDB(product);
      }
      addOne(product)();
    },
    [addOne, count, onSaveOverLimitInDB],
  );

  const { saveForLater } = useSaveForLater();
  const { isOverLimitProduct } = useOverLimitProduct(product?.product_id);
  const { removeProductFromKit } = useKitsProducts();
  const { onDeleteClick } = useKitActions(kit);
  const computedAvailability = useUnavailableForOrderProduct(
    product,
    false,
    false,
    false,
    fromSharedKit,
  );
  const { isUnavailableForOrder, unavailableLabel, unavailableWatermark } =
    getUnavailability(kitItemOrderable, computedAvailability);

  const { product_id: stockNumber, prepack_quantity: perUnitQty } =
    product || {};

  const { classes, cx } = useStyles({
    isUnavailableForOrder,
    isKiteUser,
    reviewOrderPage,
    placedOrder: !!placedOrder?.id,
  });

  const isRecurring = !!(placedOrder && placedOrder.schedule);

  const pendingApproval = useMemo(
    () => getPendingApproval(isRecurring, placedOrder, stockNumber),
    [isRecurring, placedOrder, stockNumber],
  );

  const orderedProduct = useMemo(
    () => getOrderedProduct(isRecurring, placedOrder?.order_items, stockNumber),
    [isRecurring, placedOrder?.order_items, stockNumber],
  );

  const onOpenModal = useCallback(() => {
    let modalId;
    if (kit) {
      modalId = 'kits-product-details';
    } else if (placedOrder?.id) {
      modalId = 'placed-order-details';
    } else {
      modalId = 'cart-product-details';
    }

    dispatch(
      OpenModal({
        Component: () => (
          <Details
            productId={product.product_id}
            addOneProduct={addOneProduct}
            removeOneProduct={removeOneProduct}
            kitItemOrderable={kitItemOrderable}
            readOnly={readOnly}
          />
        ),
        id: modalId,
        props: {
          showRequestModal:
            !isProductMoreThanInStock &&
            window?.location?.pathname?.includes('cart'),
          withMyRating: reviewOrderPage ? !!placedOrder.id : true,
          isOverLimit: isOverLimitProduct,
          productId: stockNumber,
          isFullScreen: isTabletMode,
          paddingContent: {
            xl: '50px 40px 40px',
            lg: '60px 28px 38px',
            md: '40px 12px 16px',
          },
        },
        styles: {
          width: 'min(90vw, 1143px)',
        },
      }),
    );
  }, [
    dispatch,
    kit,
    placedOrder,
    isProductMoreThanInStock,
    reviewOrderPage,
    isOverLimitProduct,
    stockNumber,
    isTabletMode,
    removeOneProduct,
    addOneProduct,
    kitItemOrderable,
    readOnly,
    product,
  ]);

  const totalItemsHeader = useMemo(
    () => getTotalItemsHeader(reviewOrderPage, isDesktopMode),
    [reviewOrderPage, isDesktopMode],
  );

  const orderedQty = orderedProduct?.qty_ordered * addressesCount;
  const totalOrderedQty =
    orderedProduct?.qty_ordered * perUnitQty * addressesCount;
  const totalItemsQty = perUnitQty * count * addressesCount;
  const distributionUnitsQty = count * addressesCount;

  return {
    isDesktopMode,
    isTabletMode,
    isMobileMode,
    count,
    addOneProduct,
    removeOneProduct,
    setCount,
    onDeleteProduct,
    saveForLater,
    isOverLimitProduct,
    removeProductFromKit,
    onDeleteClick,
    isUnavailableForOrder,
    unavailableLabel,
    unavailableWatermark,
    classes,
    cx,
    isRecurring,
    pendingApproval,
    orderedProduct,
    onOpenModal,
    totalItemsHeader,
    orderedQty,
    totalOrderedQty,
    totalItemsQty,
    distributionUnitsQty,
  };
};

export default useCartCard;
